import {
  Box,
  Text,
  IconButton,
  FormControl,
  InputGroup,
  Input,
  FormErrorMessage,
  Flex,
  VisuallyHidden,
  InputLeftElement,
  Icon,
} from '@everlywell/ui-kit';
import { Tag, Trash, Plus } from '@phosphor-icons/react';
import AppliedPromos from 'components/Cart/components/AppliedPromos/AppliedPromos';
import { PROMOCODE_PARAM } from 'components/CheckoutML/hooks/useUpdateOrderWithUrlParams/useUpdateOrderWithUrlParams';
import { PromoFormValues } from 'components/PromoCode/PromoCode';
import React, { MouseEventHandler } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import analytics from 'utils/analytics';
import { BaseRequestError } from 'utils/api/request';
import { ANALYTICS } from 'utils/constants/analytics';
import { STORE_ROOT } from 'utils/constants/urls';
import { AxiosErrorResponse, Order } from 'utils/types';

type PromoCodeSubmissionProps = {
  promoMessage: string;
  promotions?: any[];
  promotion?: string | null; // Deprecated: kept for backward compatibility
  order?: Order;
  showLoadingState?: boolean;
  isLoading?: boolean;
  applyPromoMutation: any;
  removePromoCodeHandler: MouseEventHandler<HTMLButtonElement>;
  removeSinglePromoHandler?: (promoCode: string) => void;
  handlePromoCodeSubmit: (event: React.FormEvent) => void;
  formState: UseFormMethods<PromoFormValues>['formState'];
  control: UseFormMethods<PromoFormValues>['control'];
  clearErrors?: UseFormMethods<PromoFormValues>['clearErrors'];
  promoCodeInputValue: string;
  setPromoCodeInputValue: (value: string) => void;
  wasAdvertisedPromoSubmitted: boolean;
  advertisedPromosElement: React.ReactNode | null;
  shouldShowInputIcon: boolean;
  /**
   * Shows lightGrey border instead of uiGrey
   */
  isVariant?: boolean;
  hidePromoCodeEntry?: boolean;
  hiddenPromoCodeEntryMessage?: string;
};

const getPromoErrorMessage = (
  formState: UseFormMethods<PromoFormValues>['formState'],
  applyPromoMutation: any,
): string | React.ReactNode => {
  const promoCodeError = formState.errors.promoCode;
  if (promoCodeError?.message && promoCodeError.type === 'manual') {
    return promoCodeError.message;
  }

  // Don't show error if mutation is successful or loading
  if (applyPromoMutation?.isSuccess || applyPromoMutation?.isLoading) {
    return '';
  }
  if (
    promoCodeError?.message &&
    (formState.isDirty || promoCodeError.type === 'manual')
  ) {
    return promoCodeError.message;
  }

  // Only show error if mutation actually failed
  if (!applyPromoMutation?.isError) {
    return '';
  }

  const mutationError = applyPromoMutation?.error as
    | AxiosErrorResponse
    | Error
    | undefined;
  if (
    mutationError instanceof TypeError ||
    (mutationError instanceof Error &&
      /failed to fetch|networkerror|load failed/i.test(mutationError.message))
  ) {
    return 'Unable to apply that coupon right now. Please check your connection and try again.';
  }

  const errors = (mutationError as AxiosErrorResponse)?.response?.data
    ?.errors;
  if ((errors?.[0] as unknown as BaseRequestError)?.message) {
    return (errors?.[0] as unknown as BaseRequestError).message;
  }

  if (errors?.[0]) {
    const message = errors[0];

    if (typeof message === 'string' && message.includes('login')) {
      const redirectUrl = new URL(`${STORE_ROOT}/login`);

      redirectUrl.searchParams.set('my_results_redirect', window.location.href);
      redirectUrl.searchParams.set(
        PROMOCODE_PARAM,
        applyPromoMutation.variables.code,
      );

      return (
        <div
          dangerouslySetInnerHTML={{
            __html: message.replace(
              'login',
              `<a href="${redirectUrl.toString()}">login</a>`,
            ),
          }}
        />
      );
    }

    return message;
  }

  return "The coupon code you entered doesn't exist. Please try again.";
};

const PromoCodeSubmission: React.FC<PromoCodeSubmissionProps> = ({
  promoMessage,
  promotions,
  promotion, // Deprecated: kept for backward compatibility
  order,
  showLoadingState,
  isLoading,
  removePromoCodeHandler,
  removeSinglePromoHandler,
  handlePromoCodeSubmit,
  control,
  applyPromoMutation,
  formState,
  clearErrors,
  advertisedPromosElement,
  wasAdvertisedPromoSubmitted,
  promoCodeInputValue,
  setPromoCodeInputValue,
  shouldShowInputIcon,
  isVariant = false,
  hidePromoCodeEntry = false,
  hiddenPromoCodeEntryMessage,
}) => {
  // Support both new (promotions array) and old (single promotion) API
  const appliedPromotions = promotions || (promotion ? [{ code: promotion }] : []);
  const isFormDirtyAndMutationError =
    formState.isDirty && applyPromoMutation.isError && !applyPromoMutation.isSuccess;
  const isFormDirtyAndHasErrorMessage =
    formState.isDirty && Boolean(formState.errors.promoCode?.message);
  const isMutationErrorAndAdvertisedPromoSubmitted =
    applyPromoMutation.isError && !applyPromoMutation.isSuccess && wasAdvertisedPromoSubmitted;
  const isManualError = formState.errors.promoCode?.type === 'manual';

  const isFormInvalid =
    isFormDirtyAndMutationError ||
    isFormDirtyAndHasErrorMessage ||
    isMutationErrorAndAdvertisedPromoSubmitted ||
    isManualError;

  const handleCodeRemoval = (event: React.MouseEvent<HTMLButtonElement>) => {
    removePromoCodeHandler(event);
    setPromoCodeInputValue('');
  };

  const trackFreePromoCodeSubmission = (promoCode: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Free promo code submitted',
        promoCode,
      },
    });
  };

  return (
    <>
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {promoMessage}
      </VisuallyHidden>
      {appliedPromotions.length > 0 && (
        <>
          <AppliedPromos
            promos={appliedPromotions.map((p) => ({
              code: p.code || p,
              id: p.id,
            }))}
            onRemove={removeSinglePromoHandler || (() => removePromoCodeHandler({} as any))}
            isLoading={isLoading}
            showLoadingState={showLoadingState}
          />
          {appliedPromotions.length === 1 && !removeSinglePromoHandler && (
            <Box
              display="flex"
              width="100%"
              gap={2}
              background="tints.white"
              padding={4}
              borderRadius={12}
              marginBottom={2}
            >
              <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
                <Box display="flex" flexDirection="column" gap={1} flexGrow="1">
                  <Text fontSize="md" fontWeight="medium">
                    {appliedPromotions[0]?.code?.toUpperCase() || appliedPromotions[0]}{' '}
                    <Text as="span" fontSize="md" color="tints.darkGrey">
                      applied
                    </Text>
                  </Text>
                </Box>
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton
                  icon={<Trash />}
                  variant="primary"
                  colorScheme="whiteCS"
                  color="utility.sale"
                  aria-label="Remove promo code"
                  size="sm"
                  padding={4}
                  marginY={-4}
                  isLoading={showLoadingState && isLoading}
                  _loading={{
                    backgroundColor: 'tints.white',
                  }}
                  sx={{
                    '& svg': {
                      fontSize: '1.25rem !important',
                    },
                  }}
                  onClick={handleCodeRemoval}
                />
              </Box>
            </Box>
          )}
        </>
      )}
      {hidePromoCodeEntry ? (
        hiddenPromoCodeEntryMessage ? (
          <Text marginTop={appliedPromotions.length > 0 ? 2 : 0}>
            {hiddenPromoCodeEntryMessage}
          </Text>
        ) : null
      ) : (
        <>
          <Box
            as="form"
            display="flex"
            noValidate
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            onSubmit={handlePromoCodeSubmit}
            marginTop={appliedPromotions.length > 0 ? 2 : 0}
          >
            <FormControl isInvalid={isFormInvalid}>
              <Controller
                control={control}
                name="promoCode"
                render={(field) => (
                  <InputGroup>
                    {shouldShowInputIcon ? (
                      <InputLeftElement pointerEvents="none">
                        <Icon as={Tag} color="tints.darkGrey" />
                      </InputLeftElement>
                    ) : null}
                    <Input
                      {...field}
                      onChange={(event) => {
                        applyPromoMutation.reset();
                        clearErrors?.('promoCode');
                        field.onChange(event);
                        setPromoCodeInputValue(event.target.value);
                      }}
                      placeholder="Add promo code"
                      name="promoCode"
                      aria-label="promoCode"
                      {...(isVariant && { borderColor: 'tints.lightGrey' })}
                      readOnly={showLoadingState && isLoading}
                      value={promoCodeInputValue || ''}
                    />
                  </InputGroup>
                )}
              />
              <FormErrorMessage
                sx={{
                  a: {
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    '&:focus': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                {getPromoErrorMessage(formState, applyPromoMutation)}
              </FormErrorMessage>
            </FormControl>
            <Flex alignItems="center">
              <IconButton
                icon={<Plus />}
                type="submit"
                variant="primary"
                colorScheme="whiteCS"
                color="tints.darkGrey"
                padding="2"
                isLoading={showLoadingState && isLoading}
                aria-label="Apply promo code"
                size="sm"
                marginTop="5px"
                onClick={() => trackFreePromoCodeSubmission(promoCodeInputValue)}
              />
            </Flex>
          </Box>

          {advertisedPromosElement}
        </>
      )}
    </>
  );
};
export default PromoCodeSubmission;
